import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  App,
  Typography,
  Avatar,
  Upload,
} from 'antd';
import {
  getModalityById,
  createModality,
  updateModality,
  checkModalityExists,
  uploadModalityMedia,
  checkModalityOrderExists,
} from '../../services/modalities';
import { LeftOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import { resolveMedia } from '../../utils/media';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;

const { useApp } = App;

const ModalityUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(undefined);
  const [originalPhotoUrl, setOriginalPhotoUrl] = useState(null);

  const { canAny } = usePermissions();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);
    getModalityById(id)
      .then((modality) => {
        form.setFieldsValue({
          description: modality.description?.trim(),
          code: modality.code?.trim(),
          name: modality.name?.trim(),
          order: modality.order ?? 0,
        });
        if (modality.icon) {
          setOriginalPhotoUrl(modality.icon);
          setAvatarPreview(resolveMedia(modality.icon));
        }
      })
      .catch(() => {
        message.error('No se pudo cargar la modalidad');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, form]);

  const uploadProps = useMemo(
    () => ({
      maxCount: 1, // Solo permitir un archivo
      beforeUpload: (file) => {
        const isImg = file.type.startsWith('image/');
        if (!isImg) {
          message.error('Solo se permiten imágenes (jpg, png, webp, etc.)');
          return Upload.LIST_IGNORE;
        }

        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
          message.error('La imagen debe pesar menos de 2MB');
          return Upload.LIST_IGNORE;
        }

        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
        return false;
      },
      onRemove: () => {
        setAvatarFile(null);
        setAvatarPreview(undefined);
      },
      showUploadList: false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * Función que se ejecuta cuando se envía el formulario
   */
  const onSubmit = async (values) => {
    try {
      setLoading(true);

      let photoUrlToSend = undefined;
      if (avatarFile) {
        const uploadResult = await uploadModalityMedia(avatarFile);
        photoUrlToSend = uploadResult.url || uploadResult;
      } else if (isEdit && originalPhotoUrl) {
        photoUrlToSend = originalPhotoUrl;
      }

      let exists = await checkModalityExists(values.description.trim(), id);
      if (exists === true) {
        message.warning(
          'Ya existe una modalidad con la descripción proporcionada',
        );
        return;
      }
      exists = await checkModalityExists(values.code.trim(), id);
      if (exists === true) {
        message.warning('Ya existe una modalidad con la clave proporcionada');
        return;
      }

      exists = await checkModalityOrderExists(values.order, id);
      if (exists === true) {
        message.warning('Ya existe una modalidad con el orden proporcionado');
        return;
      }

      if (isEdit && id) {
        const updateData = {
          description: values.description.trim(),
          code: values.code.trim(),
          name: values.name.trim(),
          order: values.order,
          ...(photoUrlToSend !== undefined && { icon: photoUrlToSend }),
        };

        await updateModality(id, updateData);
        message.success('Modalidad actualizada correctamente');
      } else {
        const createData = {
          description: values.description.trim(),
          code: values.code.trim(),
          name: values.name.trim(),
          order: values.order,
          ...(photoUrlToSend !== undefined && { icon: photoUrlToSend }),
        };
        await createModality(createData);
        message.success('Modalidad agregada.');
      }
      navigate(`/modalities/all`, { replace: true });
    } catch (err) {
      // Extraer datos del error
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage =
        errorObj?.message ||
        errorData?.message ||
        err?.message ||
        'Error al guardar el usuario';

      // Si es un error de validación (422), mostrar errores en los campos del formulario
      if (
        status === 422 &&
        (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)
      ) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];

        console.log('Issues encontrados:', issues);

        if (issues.length > 0) {
          // Limpiar errores previos
          form.setFields([]);

          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message)
            .map((issue) => {
              let fieldPath = issue.path;

              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }

              console.log(
                'Mapeando error para campo:',
                fieldPath,
                'Mensaje:',
                issue.message,
              );

              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });

          console.log('Field errors a establecer:', fieldErrors);

          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
            console.log('Errores establecidos en el formulario');
          }

          message.error(
            errorMessage || 'Por favor, corrige los errores en el formulario',
          );
        } else {
          message.error(errorMessage);
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/modalities/all');
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button
          type="text"
          icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />}
          style={{
            padding: 0,
            color: '#BA955E',
            fontSize: '24px',
            fontWeight: '700',
            height: 'auto',
          }}
          onClick={handleBack}
        />
        <Title level={3} style={{ marginBottom: 0 }}>
          {isEdit ? 'Editar modalidad' : 'Agregar modalidad'}
        </Title>
      </div>
      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Modalidad"
                name="name"
                rules={[
                  { required: true, message: 'La modalidad es requerida' },
                ]}
              >
                <Input placeholder="modalidad" allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Clave"
                name="code"
                rules={[{ required: true, message: 'La clave es requerida' }]}
              >
                <Input placeholder="clave" allowClear />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Descripción corta"
                name="description"
                rules={[
                  { required: true, message: 'La descripción es requerida' },
                  { max: 255, message: 'No se aceptan más de 255 caracteres' },
                ]}
              >
                <Input placeholder="descripción corta" allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Icono">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Avatar con preview de la imagen */}
                  <Avatar
                    size={64}
                    src={avatarPreview}
                    icon={<UserOutlined />}
                  />
                  {/* Botón para seleccionar imagen */}
                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>Elige una imagen</Button>
                  </Upload>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Orden"
                name="order"
                type="number"
                min={1}
                rules={[{ required: true, message: 'La orden es requerida' }]}
              >
                <Input placeholder="orden" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={() => navigate(-1)} disabled={loading}>
                Cancelar
              </Button>
            </Col>
            {canAny(['modalities.create', 'modalities.update']) && (
              <Col>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {isEdit ? 'Guardar cambios' : 'Guardar'}
                </Button>
              </Col>
            )}
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default ModalityUpsertPage;
